import { pickT } from "@repo/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./asset-allocation-form.i18n";
import type { ComponentFormProps } from "./types";

export type AssetAllocationConfig = {
  title?: string;
  description?: string;
  showChart: boolean;
  showTable: boolean;
  /**
   * QP-177 — render existing-vs-modified comparison for followup sessions.
   * Strict opt-in: only explicit `true` enables it.
   */
  enableFollowupComparison?: boolean;
};

type AssetAllocationFormProps = ComponentFormProps<AssetAllocationConfig>;

export function AssetAllocationForm({ config, onChange }: AssetAllocationFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="asset-alloc-title">{t.title}</Label>
        <Input
          id="asset-alloc-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="asset-alloc-description">{t.description}</Label>
        <Textarea
          id="asset-alloc-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="asset-alloc-show-chart" className="text-base">
            {t.showChart}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showChartHelp}</p>
        </div>
        <Switch
          id="asset-alloc-show-chart"
          checked={config.showChart !== false}
          onCheckedChange={(checked) => onChange({ ...config, showChart: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="asset-alloc-show-table" className="text-base">
            {t.showTable}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showTableHelp}</p>
        </div>
        <Switch
          id="asset-alloc-show-table"
          checked={config.showTable || false}
          onCheckedChange={(checked) => onChange({ ...config, showTable: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="asset-alloc-followup-comparison" className="text-base">
            {t.enableFollowupComparison}
          </Label>
          <p className="text-sm text-muted-foreground">{t.enableFollowupComparisonHelp}</p>
        </div>
        <Switch
          id="asset-alloc-followup-comparison"
          checked={config.enableFollowupComparison === true}
          onCheckedChange={(checked) => onChange({ ...config, enableFollowupComparison: checked })}
        />
      </div>

      {!config.showChart && !config.showTable && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.validationError}
        </div>
      )}
    </div>
  );
}

export function validateAssetAllocationConfig(
  config: AssetAllocationConfig,
  lang = "en_GB",
): string | null {
  const t = pickT(T, lang);
  if (!config.showChart && !config.showTable) {
    return t.validationErrorLong;
  }
  return null;
}

export function getDefaultAssetAllocationConfig(): AssetAllocationConfig {
  return {
    title: "Asset Allocation",
    description: "Distribution of investments across asset classes.",
    showChart: true,
    showTable: true,
  };
}
